import GoogleIcon from '@mui/icons-material/Google';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useGoogleLogin } from '@react-oauth/google';
import { authService } from 'api/services/authService';
import { useAuthStore } from 'features/auth/stores/authStore';
import React, { useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from 'shared/constants/routes';

const LoginPage: React.FC = () => {
  const { isAuthenticated, setAuth } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(searchParams.get('error'));

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTE_PATHS.TRACKER;

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      setLoading(true);
      setError(null);
      try {
        // Exchange code with backend
        const response = await authService.googleLogin({ code: codeResponse.code });
        const { accessToken, refreshToken } = response.data.data;

        // Store tokens
        setAuth(accessToken, refreshToken, null);

        // Fetch user profile
        const meResponse = await authService.getMe();
        const userData = meResponse.data.data;

        const user = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          avatarUrl: userData.avatarUrl,
          role: (userData.role || 'TeamMember') as
            | 'Owner'
            | 'Admin'
            | 'ProjectManager'
            | 'TeamMember',
          workspaceId: userData.workspaceId || '',
        };

        setAuth(accessToken, refreshToken, user);
      } catch (err: any) {
        const message = err?.message || err?.errors?.ProviderEmail?.[0] || 'Sign in failed';
        setError(message);
        setLoading(false);
      }
    },
    onError: (errorResponse) => {
      setError(errorResponse.error_description || 'Google sign in was cancelled');
    },
  });

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" color="primary" fontWeight={700} gutterBottom>
            ZeLog
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Track your time, manage your projects
          </Typography>
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
            onClick={() => googleLogin()}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
